/**
 * Otiumberg laser engraving preview engine (standalone).
 * Upload black-on-white artwork → threshold mask → etch composite on pendant.
 * Exposes window.OtiumbergLaserEngraving for the test page and later PDP overlay.
 */
(function (global) {
  'use strict';

  var DEFAULTS = {
    // Keep well under Safari/Chrome canvas + ImageData limits.
    // Display card is ~400px; lower caps keep detailed uploads interactive.
    maxUploadPx: 1100,
    maxBasePx: 1200,
    maxLineWorkPx: 900,
    // Working size while dragging / pinching (full quality on gesture end)
    interactiveMaxPx: 640,
    interactiveMaskPx: 420,
    threshold: 0.4,
    despeckle: true,
    marginPercent: 4,
    etchOpacity: 0.9,
    // Laser stroke thickness as % of disc diameter (constant in pendant space —
    // does not change when the customer scales artwork).
    strokeWidthPercent: 2,
    // Customer artwork placement (1 = fit inside disc; offsets are fractions of the fit box)
    artworkScale: 1,
    artworkOffsetX: 0,
    artworkOffsetY: 0,
    artworkRotation: 0, // degrees, clockwise positive
    disc: {
      cxPercent: 50,
      cyPercent: 50,
      // New base: 400px artboard, disc face ~180px → 45%
      diameterPercent: 45
    }
  };

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function mergeOptions(opts) {
    opts = opts || {};
    var disc = Object.assign({}, DEFAULTS.disc, opts.disc || {});
    return Object.assign({}, DEFAULTS, opts, { disc: disc });
  }

  function loadImage(src, options) {
    options = options || {};
    return new Promise(function (resolve, reject) {
      if (!src) {
        reject(new Error('No image source'));
        return;
      }

      var img = new Image();
      var isRemote = /^https?:/i.test(src) || src.indexOf('//') === 0;
      // Never set crossOrigin on blob:/data: — Safari rejects those loads.
      // Remote CDN images need it so canvas export stays untainted.
      if (isRemote && options.crossOrigin !== false) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        // Retry once without CORS if a remote asset blocked anonymous load.
        if (isRemote && options.crossOrigin !== false && options._retried !== true) {
          loadImage(src, { crossOrigin: false, _retried: true })
            .then(resolve)
            .catch(reject);
          return;
        }
        reject(new Error(options.errorMessage || 'Failed to load image'));
      };
      img.src = src;
    });
  }

  function loadImageFromFile(file) {
    return new Promise(function (resolve, reject) {
      if (!file) {
        reject(new Error('No file'));
        return;
      }
      if (!file.type || file.type.indexOf('image/') !== 0) {
        reject(new Error('Please choose an image file (PNG, JPG or WebP).'));
        return;
      }
      var url = URL.createObjectURL(file);
      // blob: URLs must not use crossOrigin
      loadImage(url, { crossOrigin: false, errorMessage: 'Failed to load uploaded image' })
        .then(function (img) {
          URL.revokeObjectURL(url);
          resolve(img);
        })
        .catch(function (err) {
          URL.revokeObjectURL(url);
          reject(err);
        });
    });
  }

  function drawScaledToCanvas(img, maxPx) {
    var w = img.naturalWidth || img.width;
    var h = img.naturalHeight || img.height;
    var scale = 1;
    if (maxPx && Math.max(w, h) > maxPx) {
      scale = maxPx / Math.max(w, h);
    }
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    if (ctx.imageSmoothingQuality) ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  /** Downscale an existing canvas (returns source if already small enough). */
  function downscaleCanvas(source, maxPx) {
    if (!source) return source;
    var w = source.width;
    var h = source.height;
    if (!maxPx || Math.max(w, h) <= maxPx) return source;
    var scale = maxPx / Math.max(w, h);
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    if (ctx.imageSmoothingQuality) ctx.imageSmoothingQuality = 'medium';
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  /** Black/dark pixels → opaque white mask; light → transparent */
  function thresholdToMask(sourceCanvas, threshold, despeckle) {
    var w = sourceCanvas.width;
    var h = sourceCanvas.height;
    var ctx = sourceCanvas.getContext('2d');
    var data = ctx.getImageData(0, 0, w, h);
    var px = data.data;
    var mask = document.createElement('canvas');
    mask.width = w;
    mask.height = h;
    var mctx = mask.getContext('2d');
    var out = mctx.createImageData(w, h);
    var opx = out.data;
    var i;
    var lum;
    var on;

    for (i = 0; i < px.length; i += 4) {
      lum = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255;
      // Treat near-white / transparent as background
      if (px[i + 3] < 20) {
        on = false;
      } else {
        on = lum < threshold;
      }
      opx[i] = 255;
      opx[i + 1] = 255;
      opx[i + 2] = 255;
      opx[i + 3] = on ? 255 : 0;
    }

    if (despeckle) {
      despeckleMask(opx, w, h);
    }

    mctx.putImageData(out, 0, 0);
    return mask;
  }

  function maskToBinary(maskCanvas) {
    var w = maskCanvas.width;
    var h = maskCanvas.height;
    var px = maskCanvas.getContext('2d').getImageData(0, 0, w, h).data;
    var bin = new Uint8Array(w * h);
    var i;
    for (i = 0; i < bin.length; i++) {
      bin[i] = px[i * 4 + 3] > 128 ? 1 : 0;
    }
    return { bin: bin, w: w, h: h };
  }

  function binaryToMask(bin, w, h) {
    var mask = document.createElement('canvas');
    mask.width = w;
    mask.height = h;
    var ctx = mask.getContext('2d');
    var img = ctx.createImageData(w, h);
    var px = img.data;
    var i;
    var o;
    for (i = 0; i < bin.length; i++) {
      o = i * 4;
      px[o] = 255;
      px[o + 1] = 255;
      px[o + 2] = 255;
      px[o + 3] = bin[i] ? 255 : 0;
    }
    ctx.putImageData(img, 0, 0);
    return mask;
  }

  function countOn(bin) {
    var n = 0;
    var i;
    for (i = 0; i < bin.length; i++) if (bin[i]) n++;
    return n;
  }

  /** Mean local thickness of ink (rough) — used to pick outline vs skeleton. */
  function estimateMeanThickness(bin, w, h) {
    var samples = 0;
    var total = 0;
    var x;
    var y;
    var i;
    var r;
    var hit;
    var step = Math.max(2, Math.floor(Math.min(w, h) / 120));

    for (y = 1; y < h - 1; y += step) {
      for (x = 1; x < w - 1; x += step) {
        i = y * w + x;
        if (!bin[i]) continue;
        // Skip interior of large fills for a fairer stroke reading — still sample some
        hit = 0;
        for (r = 1; r <= 24; r++) {
          if (
            x - r < 0 ||
            x + r >= w ||
            y - r < 0 ||
            y + r >= h ||
            !bin[i - r] ||
            !bin[i + r] ||
            !bin[i - r * w] ||
            !bin[i + r * w]
          ) {
            hit = r;
            break;
          }
        }
        if (!hit) hit = 24;
        total += hit * 2;
        samples++;
        if (samples > 400) break;
      }
      if (samples > 400) break;
    }
    return samples ? total / samples : 1;
  }

  /** 1px boundary of a binary shape (for thick fills / icons). */
  function morphologicalOutline(bin, w, h) {
    var out = new Uint8Array(w * h);
    var x;
    var y;
    var i;
    var edge;
    for (y = 1; y < h - 1; y++) {
      for (x = 1; x < w - 1; x++) {
        i = y * w + x;
        if (!bin[i]) continue;
        edge =
          !bin[i - 1] ||
          !bin[i + 1] ||
          !bin[i - w] ||
          !bin[i + w] ||
          !bin[i - w - 1] ||
          !bin[i - w + 1] ||
          !bin[i + w - 1] ||
          !bin[i + w + 1];
        if (edge) out[i] = 1;
      }
    }
    return out;
  }

  /**
   * Zhang–Suen thinning — collapses any stroke/fill to a 1px centreline skeleton.
   */
  function skeletonizeZhangSuen(bin, w, h) {
    var grid = new Uint8Array(bin);
    var changed = true;
    var iter;
    var maxIter = 40;

    function at(x, y) {
      if (x < 0 || y < 0 || x >= w || y >= h) return 0;
      return grid[y * w + x];
    }

    function neighbours(x, y) {
      // p2..p9 clockwise starting at north
      return [
        at(x, y - 1),
        at(x + 1, y - 1),
        at(x + 1, y),
        at(x + 1, y + 1),
        at(x, y + 1),
        at(x - 1, y + 1),
        at(x - 1, y),
        at(x - 1, y - 1)
      ];
    }

    function transitions(n) {
      var t = 0;
      var k;
      for (k = 0; k < 8; k++) {
        if (n[k] === 0 && n[(k + 1) % 8] === 1) t++;
      }
      return t;
    }

    function sum(n) {
      return n[0] + n[1] + n[2] + n[3] + n[4] + n[5] + n[6] + n[7];
    }

    for (iter = 0; iter < maxIter && changed; iter++) {
      changed = false;
      [0, 1].forEach(function (step) {
        var remove = [];
        var x;
        var y;
        var n;
        var s;
        var a;
        for (y = 1; y < h - 1; y++) {
          for (x = 1; x < w - 1; x++) {
            if (!grid[y * w + x]) continue;
            n = neighbours(x, y);
            s = sum(n);
            if (s < 2 || s > 6) continue;
            a = transitions(n);
            if (a !== 1) continue;
            if (step === 0) {
              if (n[0] * n[2] * n[4] !== 0) continue;
              if (n[2] * n[4] * n[6] !== 0) continue;
            } else {
              if (n[0] * n[2] * n[6] !== 0) continue;
              if (n[0] * n[4] * n[6] !== 0) continue;
            }
            remove.push(y * w + x);
          }
        }
        if (remove.length) {
          changed = true;
          remove.forEach(function (idx) {
            grid[idx] = 0;
          });
        }
      });
    }
    return grid;
  }

  /** Circular brush dilate — hard binary (legacy). */
  function dilateBinary(bin, w, h, radius) {
    radius = Math.max(1, Math.round(radius));
    if (radius <= 1) {
      var thin = new Uint8Array(bin);
      var x;
      var y;
      var i;
      for (y = 1; y < h - 1; y++) {
        for (x = 1; x < w - 1; x++) {
          i = y * w + x;
          if (!bin[i]) continue;
          thin[i] = 1;
          thin[i - 1] = 1;
          thin[i + 1] = 1;
          thin[i - w] = 1;
          thin[i + w] = 1;
        }
      }
      return thin;
    }

    var out = new Uint8Array(w * h);
    var r2 = radius * radius;
    var dx;
    var dy;
    var x;
    var y;
    var nx;
    var ny;
    var i;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        i = y * w + x;
        if (!bin[i]) continue;
        for (dy = -radius; dy <= radius; dy++) {
          for (dx = -radius; dx <= radius; dx++) {
            if (dx * dx + dy * dy > r2) continue;
            nx = x + dx;
            ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            out[ny * w + nx] = 1;
          }
        }
      }
    }
    return out;
  }

  function smoothstep(edge0, edge1, x) {
    var t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
  }

  /** Nearest-neighbour upsample of a binary mask (keeps strokes crisp). */
  function upsampleBinaryNearest(bin, w, h, scale) {
    scale = Math.max(1, Math.round(scale));
    if (scale === 1) return { bin: bin, w: w, h: h };
    var nw = w * scale;
    var nh = h * scale;
    var out = new Uint8Array(nw * nh);
    var x;
    var y;
    var sx;
    var sy;
    for (y = 0; y < nh; y++) {
      sy = Math.floor(y / scale);
      for (x = 0; x < nw; x++) {
        sx = Math.floor(x / scale);
        out[y * nw + x] = bin[sy * w + sx];
      }
    }
    return { bin: out, w: nw, h: nh };
  }

  /**
   * Convert threshold mask → 1px centreline skeleton.
   * Stroke thickness is applied later in disc/pendant pixels so scale stays constant.
   */
  function redrawAsEngravedLines(maskCanvas, options) {
    options = mergeOptions(options);
    var packed = maskToBinary(maskCanvas);
    var bin = packed.bin;
    var w = packed.w;
    var h = packed.h;
    var onCount = countOn(bin);
    if (!onCount) return maskCanvas;

    var density = onCount / (w * h);
    // Dense / photo-like masks: keep line-work smaller so skeleton + stamp stay usable
    var maxWork = options.maxLineWorkPx || 900;
    if (density > 0.12) maxWork = Math.min(maxWork, 520);
    else if (density > 0.06) maxWork = Math.min(maxWork, 700);

    var shortSide = Math.min(w, h);
    // Only upsample sparse line art — dense fills get slower and crashier when enlarged
    if (shortSide < maxWork && density < 0.04) {
      packed = upsampleBinaryNearest(
        bin,
        w,
        h,
        Math.min(2, Math.ceil(maxWork / shortSide))
      );
      bin = packed.bin;
      w = packed.w;
      h = packed.h;
      shortSide = Math.min(w, h);
    } else if (shortSide > maxWork) {
      var down = Math.max(1, Math.ceil(shortSide / maxWork));
      var nw = Math.max(1, Math.floor(w / down));
      var nh = Math.max(1, Math.floor(h / down));
      var shrunk = new Uint8Array(nw * nh);
      var x;
      var y;
      for (y = 0; y < nh; y++) {
        for (x = 0; x < nw; x++) {
          shrunk[y * nw + x] = bin[y * down * w + x * down];
        }
      }
      bin = shrunk;
      w = nw;
      h = nh;
      shortSide = Math.min(w, h);
    }

    // Reference thickness only to decide outline-vs-fill collapse (not final stroke)
    var targetStroke = Math.max(2, Math.round(shortSide * 0.008));
    var thickness = estimateMeanThickness(bin, w, h);

    var source = bin;
    if (thickness > targetStroke * 2.2) {
      source = morphologicalOutline(bin, w, h);
      if (!countOn(source)) source = bin;
    }

    var skeleton = skeletonizeZhangSuen(source, w, h);
    if (!countOn(skeleton)) {
      return maskCanvas;
    }

    return binaryToMask(skeleton, w, h);
  }

  function despeckleMask(px, w, h) {
    var copy = new Uint8ClampedArray(px);
    var x;
    var y;
    var i;
    var n;
    var dx;
    var dy;
    var nx;
    var ny;
    var ni;
    for (y = 1; y < h - 1; y++) {
      for (x = 1; x < w - 1; x++) {
        i = (y * w + x) * 4 + 3;
        if (copy[i] < 128) continue;
        n = 0;
        for (dy = -1; dy <= 1; dy++) {
          for (dx = -1; dx <= 1; dx++) {
            if (!dx && !dy) continue;
            nx = x + dx;
            ny = y + dy;
            ni = (ny * w + nx) * 4 + 3;
            if (copy[ni] > 128) n++;
          }
        }
        if (n < 2) {
          px[i] = 0;
        }
      }
    }
  }

  function createBlankPendantCanvas(size) {
    size = size || 900;
    var canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    var ctx = canvas.getContext('2d');

    ctx.fillStyle = '#f3eee8';
    ctx.fillRect(0, 0, size, size);

    var cx = size / 2;
    var cy = size * 0.5;
    var r = size * 0.225; // 45% diameter

    // Soft drop shadow
    ctx.save();
    ctx.shadowColor = 'rgba(80, 60, 40, 0.22)';
    ctx.shadowBlur = size * 0.04;
    ctx.shadowOffsetY = size * 0.012;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#e8c46a';
    ctx.fill();
    ctx.restore();

    // Disc body with radial gold
    var grad = ctx.createRadialGradient(
      cx - r * 0.35,
      cy - r * 0.4,
      r * 0.1,
      cx,
      cy,
      r
    );
    grad.addColorStop(0, '#f6e2a8');
    grad.addColorStop(0.45, '#e0b85a');
    grad.addColorStop(0.85, '#c9963a');
    grad.addColorStop(1, '#a87828');
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();

    // Rim highlight
    ctx.beginPath();
    ctx.arc(cx, cy, r - 1, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255, 245, 210, 0.55)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Bail hint at top
    ctx.beginPath();
    ctx.arc(cx, cy - r + size * 0.018, size * 0.012, 0, Math.PI * 2);
    ctx.fillStyle = '#d4a84a';
    ctx.fill();

    return {
      canvas: canvas,
      disc: {
        cxPercent: 50,
        cyPercent: 50,
        diameterPercent: 45
      }
    };
  }

  /**
   * Fit artwork into the disc bounding box without stretching (contain + centre),
   * then apply optional customer scale / offset.
   * artworkScale: 1 = contain; artworkOffsetX/Y: fractions of boxSize from centre.
   */
  function fitArtRect(maskCanvas, boxX, boxY, boxSize, options) {
    options = options || {};
    var mw = Math.max(1, maskCanvas.width || options.maskWidth || 1);
    var mh = Math.max(1, maskCanvas.height || options.maskHeight || 1);
    var scaleMul = clamp(
      options.artworkScale != null ? options.artworkScale : 1,
      0.25,
      3
    );
    var scale = Math.min(boxSize / mw, boxSize / mh) * scaleMul;
    var dw = mw * scale;
    var dh = mh * scale;
    var ox = (options.artworkOffsetX || 0) * boxSize;
    var oy = (options.artworkOffsetY || 0) * boxSize;
    return {
      x: boxX + (boxSize - dw) / 2 + ox,
      y: boxY + (boxSize - dh) / 2 + oy,
      w: dw,
      h: dh
    };
  }

  /**
   * Tight bounds around opaque mask pixels (ignores empty upload margins).
   */
  function getMaskContentBounds(maskCanvas, alphaThreshold) {
    alphaThreshold = alphaThreshold == null ? 20 : alphaThreshold;
    var w = maskCanvas.width;
    var h = maskCanvas.height;
    var ctx = maskCanvas.getContext('2d');
    var data = ctx.getImageData(0, 0, w, h).data;
    var minX = w;
    var minY = h;
    var maxX = -1;
    var maxY = -1;
    var x;
    var y;
    var a;
    var i;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        a = data[(y * w + x) * 4 + 3];
        if (a > alphaThreshold) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) {
      return { x: 0, y: 0, w: w, h: h };
    }
    var pad = Math.max(2, Math.round(Math.min(w, h) * 0.01));
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);
    return {
      x: minX,
      y: minY,
      w: Math.max(1, maxX - minX + 1),
      h: Math.max(1, maxY - minY + 1)
    };
  }

  /**
   * Disc inner box + artwork rect in pendant canvas pixels (for UI overlays).
   * Pass contentBounds (from getMaskContentBounds) to also get contentRect.
   */
  function getPlacementLayout(baseWidth, baseHeight, maskWidth, maskHeight, options, contentBounds) {
    options = mergeOptions(options);
    var w = baseWidth;
    var h = baseHeight;
    var cx = (options.disc.cxPercent / 100) * w;
    var cy = (options.disc.cyPercent / 100) * h;
    var diameter = (options.disc.diameterPercent / 100) * Math.min(w, h);
    var radius = diameter / 2;
    var margin = clamp(options.marginPercent, 0, 40) / 100;
    var inner = radius * (1 - margin);
    var boxSize = inner * 2;
    var boxX = cx - inner;
    var boxY = cy - inner;
    var artRect = fitArtRect(
      { width: maskWidth, height: maskHeight },
      boxX,
      boxY,
      boxSize,
      options
    );
    var contentRect = null;
    if (contentBounds && maskWidth > 0 && maskHeight > 0) {
      var sx = artRect.w / maskWidth;
      var sy = artRect.h / maskHeight;
      contentRect = {
        x: artRect.x + contentBounds.x * sx,
        y: artRect.y + contentBounds.y * sy,
        w: contentBounds.w * sx,
        h: contentBounds.h * sy
      };
    }
    return {
      cx: cx,
      cy: cy,
      radius: radius,
      box: { x: boxX, y: boxY, size: boxSize },
      artRect: artRect,
      contentRect: contentRect,
      rotation: options.artworkRotation || 0
    };
  }

  /**
   * Place artwork centreline onto the pendant with a V-groove depth profile.
   * Densifies skeleton segments in pendant space (avoids stair-steps when scaled)
   * and uses a hard-edged brush so soft “halos” don’t read as pixelated AA.
   */
  function placeMaskOnCanvas(w, h, maskCanvas, artRect, cx, cy, radius, strokeRadius, rotationDeg, interactive) {
    var layer = document.createElement('canvas');
    layer.width = w;
    layer.height = h;
    var lctx = layer.getContext('2d');

    var mw = Math.max(1, maskCanvas.width);
    var mh = Math.max(1, maskCanvas.height);
    var mdata = maskCanvas.getContext('2d').getImageData(0, 0, mw, mh).data;
    var on = new Uint8Array(mw * mh);
    var x;
    var y;
    var i;
    var onCount = 0;
    for (y = 0; y < mh; y++) {
      for (x = 0; x < mw; x++) {
        if (mdata[(y * mw + x) * 4 + 3] >= 128) {
          on[y * mw + x] = 1;
          onCount++;
        }
      }
    }

    var depth = new Float32Array(w * h);
    var brushCap = interactive ? 10 : 24;
    var brush = Math.max(2, Math.min(brushCap, strokeRadius || 2));
    var brushSq = brush * brush;
    var scaleX = artRect.w / mw;
    var scaleY = artRect.h / mh;
    var discR2 = radius * radius;
    var rot = ((rotationDeg || 0) * Math.PI) / 180;
    var cos = Math.cos(rot);
    var sin = Math.sin(rot);
    var artCx = artRect.x + artRect.w / 2;
    var artCy = artRect.y + artRect.h / 2;
    // Dense masks: sample fewer centres during interactive previews
    var skip = 1;
    if (interactive && onCount > 12000) skip = 3;
    else if (interactive && onCount > 6000) skip = 2;
    else if (!interactive && onCount > 40000) skip = 2;

    function mapMaskToDest(mx, my) {
      var lx = (mx + 0.5) * scaleX - artRect.w / 2;
      var ly = (my + 0.5) * scaleY - artRect.h / 2;
      return {
        x: artCx + lx * cos - ly * sin,
        y: artCy + lx * sin + ly * cos
      };
    }

    function stampCentre(fx, fy) {
      var x0 = Math.floor(fx - brush);
      var x1 = Math.ceil(fx + brush);
      var y0 = Math.floor(fy - brush);
      var y1 = Math.ceil(fy + brush);
      var qx;
      var qy;
      var ddx;
      var ddy;
      var distSq;
      var dist;
      var t;
      var falloff;
      var idx;
      for (qy = y0; qy <= y1; qy++) {
        if (qy < 0 || qy >= h) continue;
        for (qx = x0; qx <= x1; qx++) {
          if (qx < 0 || qx >= w) continue;
          // Distance to pixel centre — include whole pixels only (no half-covered fringe)
          ddx = qx + 0.5 - fx;
          ddy = qy + 0.5 - fy;
          distSq = ddx * ddx + ddy * ddy;
          if (distSq > brushSq) continue;
          if ((qx - cx) * (qx - cx) + (qy - cy) * (qy - cy) > discR2) continue;
          dist = Math.sqrt(distSq);
          t = 1 - dist / brush;
          // Linear-ish V: 1 on centreline → low at walls (drives the light mid-line)
          falloff = Math.max(0.12, t);
          idx = qy * w + qx;
          if (falloff > depth[idx]) depth[idx] = falloff;
        }
      }
    }

    function stampSegment(x0, y0, x1, y1) {
      var dist = Math.hypot(x1 - x0, y1 - y0);
      // Sample denser than 1px so scaled-up skeletons don’t stair-step (lighter while dragging)
      var steps = Math.max(1, Math.ceil(dist * (interactive ? 1 : 2.5)));
      var s;
      var t;
      for (s = 0; s <= steps; s++) {
        t = s / steps;
        stampCentre(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
      }
    }

    var n;
    var nx;
    var ny;
    var p0;
    var p1;
    var ndx;
    var ndy;
    // 8-neighbour offsets — only walk +direction to stamp each edge once
    var neigh = [
      [1, 0],
      [1, 1],
      [0, 1],
      [-1, 1]
    ];

    for (y = 0; y < mh; y++) {
      for (x = 0; x < mw; x++) {
        if (!on[y * mw + x]) continue;
        if (skip > 1 && ((x + y) % skip) !== 0) continue;
        p0 = mapMaskToDest(x, y);
        stampCentre(p0.x, p0.y);
        for (n = 0; n < neigh.length; n++) {
          ndx = neigh[n][0];
          ndy = neigh[n][1];
          nx = x + ndx;
          ny = y + ndy;
          if (nx < 0 || ny < 0 || nx >= mw || ny >= mh) continue;
          if (!on[ny * mw + nx]) continue;
          p1 = mapMaskToDest(nx, ny);
          stampSegment(p0.x, p0.y, p1.x, p1.y);
        }
      }
    }

    var img = lctx.createImageData(w, h);
    var pxOut = img.data;
    var v;
    var idx;
    for (i = 0; i < depth.length; i++) {
      v = depth[i];
      if (v < 0.15) continue;
      idx = i * 4;
      // R = groove depth for lighting; A = fully opaque (no half-pixel fringe)
      pxOut[idx] = Math.round(v * 255);
      pxOut[idx + 1] = 255;
      pxOut[idx + 2] = 255;
      pxOut[idx + 3] = 255;
    }
    lctx.putImageData(img, 0, 0);
    return layer;
  }

  // Engraving tone ramp sampled from real laser-engraved gold refs
  var ETCH_PALETTE = {
    shadow: [36, 22, 10], // deep trough accents
    midShadow: [96, 68, 38],
    midLit: [176, 136, 78],
    highlight: [252, 236, 204], // bright centreline catch
    // Vertical wall gradient — clearly darker, wide top→bottom contrast
    wallTop: [168, 132, 62],
    wallBottom: [92, 64, 24]
  };

  function mixRgb(a, b, t) {
    return [
      a[0] + (b[0] - a[0]) * t,
      a[1] + (b[1] - a[1]) * t,
      a[2] + (b[2] - a[2]) * t
    ];
  }

  /** Map lighting shade 0..1 across the 4-tone engraving palette. */
  function etchToneFromShade(shade) {
    shade = clamp(shade, 0, 1);
    // Contrast curve — push mids apart so grooves don't read as flat tan
    shade = Math.pow(shade, 1.35);
    var p = ETCH_PALETTE;
    if (shade < 0.28) {
      return mixRgb(p.shadow, p.midShadow, shade / 0.28);
    }
    if (shade < 0.62) {
      return mixRgb(p.midShadow, p.midLit, (shade - 0.28) / 0.34);
    }
    return mixRgb(p.midLit, p.highlight, (shade - 0.62) / 0.38);
  }

  /**
   * Shade engraved strokes like real laser grooves on gold:
   * dark walls + a lighter cream line along the centre of each stroke.
   */
  function buildLitGrooveLayer(placedMask, baseCanvas, strength) {
    var w = placedMask.width;
    var h = placedMask.height;
    var mpx = placedMask.getContext('2d').getImageData(0, 0, w, h).data;
    var bpx = baseCanvas.getContext('2d').getImageData(0, 0, w, h).data;

    var height = new Float32Array(w * h);
    var coverage = new Float32Array(w * h);
    var i;
    for (i = 0; i < height.length; i++) {
      if (mpx[i * 4 + 3] < 128) {
        height[i] = 0;
        coverage[i] = 0;
        continue;
      }
      height[i] = mpx[i * 4] / 255;
      coverage[i] = 1;
    }

    // Key light ~ upper-left (matches product photography)
    var lx = -0.55;
    var ly = -0.65;
    var lz = 0.52;
    var llen = Math.sqrt(lx * lx + ly * ly + lz * lz);
    lx /= llen;
    ly /= llen;
    lz /= llen;

    var fx = 0.45;
    var fy = 0.3;
    var fz = 0.84;
    var flen = Math.sqrt(fx * fx + fy * fy + fz * fz);
    fx /= flen;
    fy /= flen;
    fz /= flen;

    var out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    var octx = out.getContext('2d');
    var img = octx.createImageData(w, h);
    var opx = img.data;

    var x;
    var y;
    var idx;
    var a;
    var tl;
    var tc;
    var tr;
    var ml;
    var mr;
    var bl;
    var bc;
    var br;
    var dx;
    var dy;
    var nx;
    var ny;
    var nz;
    var nlen;
    var ndotl;
    var ndotf;
    var ndoth;
    var spec;
    var shade;
    var bi;
    var br0;
    var bg0;
    var bb0;
    var tone;
    var r;
    var g;
    var b;
    var oi;
    var hi = ETCH_PALETTE.highlight;
    var wallTop = ETCH_PALETTE.wallTop;
    var wallBottom = ETCH_PALETTE.wallBottom;
    var hVal;
    var wall;
    var center;
    var centerBoost;
    var wallTone;
    var yNorm;

    for (y = 1; y < h - 1; y++) {
      // Ease the vertical gradient so the shift reads more clearly
      yNorm = h > 1 ? y / (h - 1) : 0;
      yNorm = Math.pow(yNorm, 0.85);
      wallTone = mixRgb(wallTop, wallBottom, yNorm);
      for (x = 1; x < w - 1; x++) {
        idx = y * w + x;
        a = coverage[idx];
        if (!a) continue;
        hVal = height[idx];

        tl = height[(y - 1) * w + (x - 1)];
        tc = height[(y - 1) * w + x];
        tr = height[(y - 1) * w + (x + 1)];
        ml = height[y * w + (x - 1)];
        mr = height[y * w + (x + 1)];
        bl = height[(y + 1) * w + (x - 1)];
        bc = height[(y + 1) * w + x];
        br = height[(y + 1) * w + (x + 1)];

        dx = -((tr + 2 * mr + br) - (tl + 2 * ml + bl)) * 4.6;
        dy = -((bl + 2 * bc + br) - (tl + 2 * tc + tr)) * 4.6;
        nz = 1;
        nlen = Math.sqrt(dx * dx + dy * dy + nz * nz) || 1;
        nx = dx / nlen;
        ny = dy / nlen;
        nz = nz / nlen;

        ndotl = Math.max(0, nx * lx + ny * ly + nz * lz);
        ndotf = Math.max(0, nx * fx + ny * fy + nz * fz);
        ndoth = Math.max(0, nx * lx + ny * ly + nz * lz);
        spec = Math.pow(ndoth, 18) * 1.1 + Math.pow(ndotf, 16) * 0.2;

        // Across-stroke: strong outer wall band, bright centreline
        wall = Math.pow(1 - hVal, 0.85);
        center = Math.pow(hVal, 2.8);

        shade = 0.06 + ndotl * 0.45 + ndotf * 0.1;
        shade = shade * (1 - wall * 0.75) + center * 0.78;
        shade = clamp(shade, 0, 1);

        tone = etchToneFromShade(shade);
        bi = idx * 4;
        br0 = bpx[bi];
        bg0 = bpx[bi + 1];
        bb0 = bpx[bi + 2];
        r = tone[0] * 0.75 + br0 * 0.25;
        g = tone[1] * 0.75 + bg0 * 0.25;
        b = tone[2] * 0.75 + bb0 * 0.25;

        // Walls dominate with vertical gold gradient (much darker than before)
        r = r * (1 - wall * 0.92) + wallTone[0] * wall * 0.92;
        g = g * (1 - wall * 0.92) + wallTone[1] * wall * 0.92;
        b = b * (1 - wall * 0.92) + wallTone[2] * wall * 0.92;

        // Extra darken on outermost wall
        r = r * (1 - wall * 0.22);
        g = g * (1 - wall * 0.22);
        b = b * (1 - wall * 0.22);

        // Explicit lighter mid-line along the groove centre
        centerBoost = center * 0.92;
        r = Math.min(255, r + centerBoost * (hi[0] - r));
        g = Math.min(255, g + centerBoost * (hi[1] - g));
        b = Math.min(255, b + centerBoost * (hi[2] - b));

        // Directional specular on the lit wall only (not washing out the mid-line)
        r = Math.min(255, r + spec * (1 - center * 0.35) * (hi[0] - r) * 0.85);
        g = Math.min(255, g + spec * (1 - center * 0.35) * (hi[1] - g) * 0.85);
        b = Math.min(255, b + spec * (1 - center * 0.35) * (hi[2] - b) * 0.85);

        oi = idx * 4;
        opx[oi] = r;
        opx[oi + 1] = g;
        opx[oi + 2] = b;
        opx[oi + 3] = Math.round(Math.min(255, 225 + 30 * strength));
      }
    }

    octx.putImageData(img, 0, 0);
    return out;
  }

  /**
   * Fast groove colouring for interactive previews — no per-pixel normals.
   */
  function buildLitGrooveLayerFast(placedMask, strength) {
    var w = placedMask.width;
    var h = placedMask.height;
    var mpx = placedMask.getContext('2d').getImageData(0, 0, w, h).data;
    var out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    var octx = out.getContext('2d');
    var img = octx.createImageData(w, h);
    var opx = img.data;
    var i;
    var hVal;
    var wall;
    var center;
    var shade;
    var tone;
    var yNorm;
    var wallTone;
    var hi = ETCH_PALETTE.highlight;
    var wallTop = ETCH_PALETTE.wallTop;
    var wallBottom = ETCH_PALETTE.wallBottom;
    var y;
    var x;
    var idx;
    for (y = 0; y < h; y++) {
      yNorm = h > 1 ? Math.pow(y / (h - 1), 0.85) : 0;
      wallTone = mixRgb(wallTop, wallBottom, yNorm);
      for (x = 0; x < w; x++) {
        idx = y * w + x;
        i = idx * 4;
        if (mpx[i + 3] < 128) continue;
        hVal = mpx[i] / 255;
        wall = Math.pow(1 - hVal, 0.85);
        center = Math.pow(hVal, 2.8);
        shade = clamp(0.2 + center * 0.7 - wall * 0.35, 0, 1);
        tone = etchToneFromShade(shade);
        opx[i] = Math.round(tone[0] * (1 - wall * 0.85) + wallTone[0] * wall * 0.85);
        opx[i + 1] = Math.round(tone[1] * (1 - wall * 0.85) + wallTone[1] * wall * 0.85);
        opx[i + 2] = Math.round(tone[2] * (1 - wall * 0.85) + wallTone[2] * wall * 0.85);
        opx[i] = Math.min(255, opx[i] + center * (hi[0] - opx[i]) * 0.7);
        opx[i + 1] = Math.min(255, opx[i + 1] + center * (hi[1] - opx[i + 1]) * 0.7);
        opx[i + 2] = Math.min(255, opx[i + 2] + center * (hi[2] - opx[i + 2]) * 0.7);
        opx[i + 3] = Math.round(255 * clamp(strength, 0.2, 1));
      }
    }
    octx.putImageData(img, 0, 0);
    return out;
  }

  /**
   * Composite etched artwork onto a pendant base canvas.
   * Uses normal-mapped groove lighting so highlights vary along each stroke.
   * Pass options.interactive for a cheaper preview while dragging.
   * Returns { canvas, width, height }
   */
  function compositeEtch(baseCanvas, maskCanvas, options) {
    options = mergeOptions(options);
    var interactive = !!options.interactive;
    var workBase = baseCanvas;
    var workMask = maskCanvas;
    if (interactive) {
      workBase = downscaleCanvas(baseCanvas, options.interactiveMaxPx || 640);
      workMask = downscaleCanvas(maskCanvas, options.interactiveMaskPx || 420);
    }

    var w = workBase.width;
    var h = workBase.height;
    var out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    var ctx = out.getContext('2d');

    ctx.drawImage(workBase, 0, 0);

    var cx = (options.disc.cxPercent / 100) * w;
    var cy = (options.disc.cyPercent / 100) * h;
    var diameter = (options.disc.diameterPercent / 100) * Math.min(w, h);
    var radius = diameter / 2;
    var margin = clamp(options.marginPercent, 0, 40) / 100;
    var inner = radius * (1 - margin);

    var boxSize = inner * 2;
    var boxX = cx - inner;
    var boxY = cy - inner;
    var artRect = fitArtRect(workMask, boxX, boxY, boxSize, options);
    var strength = clamp(options.etchOpacity, 0.2, 1);
    // Constant laser kerf — enforce a visible minimum so stale section
    // settings can’t leave strokes looking unchanged.
    var strokePct = Math.max(options.strokeWidthPercent || 2, 1.45);
    var strokePx = Math.max(
      interactive ? 3 : 4,
      Math.round((diameter * strokePct) / 100)
    );
    var strokeRadius = Math.max(2, Math.round(strokePx / 2));

    var placed = placeMaskOnCanvas(
      w,
      h,
      workMask,
      artRect,
      cx,
      cy,
      radius,
      strokeRadius,
      options.artworkRotation || 0,
      interactive
    );

    if (interactive) {
      var litFast = buildLitGrooveLayerFast(placed, strength);
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 0.97 * strength;
      ctx.drawImage(litFast, 0, 0);
      ctx.restore();
      return { canvas: out, width: w, height: h, interactive: true };
    }

    var lit = buildLitGrooveLayer(placed, workBase, strength);

    // Soft multiply on walls only — same vertical gold gradient as wall tone
    var trough = document.createElement('canvas');
    trough.width = w;
    trough.height = h;
    var tctx = trough.getContext('2d');
    var placedData = placed.getContext('2d').getImageData(0, 0, w, h);
    var troughImg = tctx.createImageData(w, h);
    var pi;
    var depthV;
    var wallA;
    var ty;
    var yNormT;
    var wallGrad;
    for (pi = 0; pi < placedData.data.length; pi += 4) {
      if (placedData.data[pi + 3] < 128) continue;
      depthV = placedData.data[pi] / 255;
      wallA = Math.pow(1 - depthV, 1.2);
      if (wallA < 0.08) continue;
      ty = Math.floor(pi / 4 / w);
      yNormT = h > 1 ? Math.pow(ty / (h - 1), 0.85) : 0;
      wallGrad = mixRgb(ETCH_PALETTE.wallTop, ETCH_PALETTE.wallBottom, yNormT);
      troughImg.data[pi] = wallGrad[0];
      troughImg.data[pi + 1] = wallGrad[1];
      troughImg.data[pi + 2] = wallGrad[2];
      troughImg.data[pi + 3] = Math.round(wallA * 235);
    }
    tctx.putImageData(troughImg, 0, 0);
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = 0.45 * strength;
    ctx.drawImage(trough, 0, 0);
    ctx.restore();

    // Lit V-groove strokes (dark walls + light centre)
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 0.97 * strength;
    ctx.drawImage(lit, 0, 0);
    ctx.restore();

    // Soft screen only on highlights
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = 0.16 * strength;
    ctx.drawImage(lit, 0, 0);
    ctx.restore();

    knockOutBlackBackdrop(out);

    return { canvas: out, width: w, height: h };
  }

  /**
   * Make near-black studio backdrop transparent.
   * Leaves the full pendant (including bail and rim) untouched.
   */
  function knockOutBlackBackdrop(canvas) {
    var ctx = canvas.getContext('2d');
    var w = canvas.width;
    var h = canvas.height;
    var data = ctx.getImageData(0, 0, w, h);
    var px = data.data;
    var i;
    var r;
    var g;
    var b;
    var maxc;
    var lum;

    for (i = 0; i < px.length; i += 4) {
      r = px[i];
      g = px[i + 1];
      b = px[i + 2];
      maxc = Math.max(r, g, b);
      lum = 0.299 * r + 0.587 * g + 0.114 * b;
      // Pure / near-black backdrop only — leave dark brown groove + metal alone
      if (maxc < 28 && lum < 22) {
        px[i + 3] = 0;
      } else if (maxc < 40 && lum < 30) {
        px[i + 3] = Math.round(px[i + 3] * ((lum - 12) / 18));
      }
    }
    ctx.putImageData(data, 0, 0);
  }

  function canvasToBlob(canvas, type, quality) {
    return new Promise(function (resolve) {
      if (canvas.toBlob) {
        canvas.toBlob(
          function (blob) {
            resolve(blob);
          },
          type || 'image/png',
          quality
        );
      } else {
        var dataUrl = canvas.toDataURL(type || 'image/png');
        var bin = atob(dataUrl.split(',')[1]);
        var arr = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(new Blob([arr], { type: type || 'image/png' }));
      }
    });
  }

  function downloadCanvas(canvas, filename) {
    return canvasToBlob(canvas).then(function (blob) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = filename || 'laser-engraving-preview.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 1000);
    });
  }

  function yieldFrame() {
    return new Promise(function (resolve) {
      setTimeout(resolve, 0);
    });
  }

  /**
   * Full pipeline from upload File + base image URL (or blank pendant).
   * Yields between heavy steps so the tab stays responsive.
   */
  function renderPreview(file, baseSrc, options) {
    options = mergeOptions(options);
    var basePromise;
    var usedBlankFallback = false;
    var maxBase = options.maxBasePx || 1600;

    if (baseSrc) {
      basePromise = loadImage(baseSrc, {
        errorMessage: 'Failed to load pendant base image'
      })
        .then(function (img) {
          return {
            canvas: drawScaledToCanvas(img, maxBase),
            disc: options.disc
          };
        })
        .catch(function () {
          usedBlankFallback = true;
          return createBlankPendantCanvas(Math.min(1200, maxBase));
        });
    } else {
      basePromise = Promise.resolve(createBlankPendantCanvas(Math.min(1200, maxBase)));
    }

    return Promise.all([loadImageFromFile(file), basePromise])
      .then(function (pair) {
        var uploadImg = pair[0];
        var base = pair[1];
        var uploadCanvas = drawScaledToCanvas(uploadImg, options.maxUploadPx);
        return yieldFrame().then(function () {
          var rawMask = thresholdToMask(uploadCanvas, options.threshold, options.despeckle);
          return yieldFrame().then(function () {
            var mask;
            try {
              mask = redrawAsEngravedLines(rawMask, options);
            } catch (err) {
              console.warn('[LaserEngraving] line redraw failed, using threshold mask', err);
              mask = rawMask;
            }
            return yieldFrame().then(function () {
              var discOpts = Object.assign({}, options, {
                disc: Object.assign({}, base.disc || DEFAULTS.disc, options.disc || {})
              });
              var result = compositeEtch(base.canvas, mask, discOpts);
              return {
                canvas: result.canvas,
                mask: mask,
                rawMask: rawMask,
                base: base.canvas,
                options: discOpts,
                usedBlankFallback: usedBlankFallback
              };
            });
          });
        });
      })
      .catch(function (err) {
        var msg = (err && err.message) || String(err || '');
        if (/memory|allocation|canvas|Maximum call/i.test(msg)) {
          throw new Error('Preview ran out of memory — try a smaller image.');
        }
        throw err;
      });
  }

  /**
   * Load pendant base (with black-backdrop knockout) for idle preview.
   */
  function prepareBasePreview(baseSrc, maxPx) {
    maxPx = maxPx || DEFAULTS.maxBasePx || 1600;
    if (!baseSrc) {
      return Promise.resolve(createBlankPendantCanvas(Math.min(1200, maxPx)).canvas);
    }
    return loadImage(baseSrc, {
      errorMessage: 'Failed to load pendant base image'
    })
      .then(function (img) {
        var canvas = drawScaledToCanvas(img, maxPx);
        knockOutBlackBackdrop(canvas);
        return canvas;
      })
      .catch(function () {
        return createBlankPendantCanvas(Math.min(1200, maxPx)).canvas;
      });
  }

  global.OtiumbergLaserEngraving = {
    defaults: DEFAULTS,
    loadImage: loadImage,
    loadImageFromFile: loadImageFromFile,
    thresholdToMask: thresholdToMask,
    redrawAsEngravedLines: redrawAsEngravedLines,
    createBlankPendantCanvas: createBlankPendantCanvas,
    prepareBasePreview: prepareBasePreview,
    fitArtRect: fitArtRect,
    getMaskContentBounds: getMaskContentBounds,
    getPlacementLayout: getPlacementLayout,
    compositeEtch: compositeEtch,
    renderPreview: renderPreview,
    canvasToBlob: canvasToBlob,
    downloadCanvas: downloadCanvas,
    mergeOptions: mergeOptions,
    downscaleCanvas: downscaleCanvas
  };
})(typeof window !== 'undefined' ? window : this);
